import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { computeAidActive, formatAidPeriod } from '../../utils/aidModeActive';
import { AID } from './AidOrnaments';

const ALLOWED_ROLES = ['admin'];

function AidSettingsPanel({ user, aidSettings, onSettingsChange }) {
  const [open,    setOpen]    = useState(false);
  const [form,    setForm]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [message, setMessage] = useState('');

  // Tous les hooks avant tout return conditionnel (Rules of Hooks)
  useEffect(() => {
    if (aidSettings) setForm({ ...aidSettings });
  }, [aidSettings]);

  // N'affiche rien aux rôles non autorisés
  if (!user || !ALLOWED_ROLES.includes(user.role)) return null;

  const handleToggle = () => {
    if (!open) setMessage('');
    setOpen(o => !o);
  };

  const patch = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/settings/aid', form);
      onSettingsChange({ ...form });
      setMessage('✅ Paramètres enregistrés');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const previewActive = form ? computeAidActive(form) : false;
  const period        = form ? formatAidPeriod(form)   : '';

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      left: 20,
      zIndex: 1100,
      fontFamily: 'inherit',
    }}>
      {/* Bouton flottant 🌙 */}
      <button
        onClick={handleToggle}
        title="Paramètres thème Aid el-Adha"
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: open
            ? `linear-gradient(135deg, ${AID.goldDeep}, ${AID.gold})`
            : '#fff',
          border: `2px solid ${AID.goldDeep}`,
          color: open ? '#fff' : AID.goldDeep,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          boxShadow: '0 3px 12px rgba(0,0,0,0.18)',
          transition: 'all 0.2s',
        }}
      >
        🌙
      </button>

      {/* Panel */}
      {open && form && (
        <div style={{
          position: 'absolute',
          bottom: 48,
          left: 0,
          width: 310,
          background: '#fffdf0',
          border: `1.5px solid ${AID.gold}`,
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(168,130,26,0.20)',
          padding: '18px 20px 14px',
        }}>
          {/* En-tête */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}>
            <div style={{
              fontWeight: 800,
              fontSize: 14,
              color: AID.brownDeep,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}>
              <span style={{ fontSize: 18 }}>🌙</span>
              Thème Aid el-Adha
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#999',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
                padding: '0 2px',
              }}
            >×</button>
          </div>

          {/* Toggle activé / désactivé */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: form.enabled ? '#fef3c7' : '#f3f4f6',
            border: `1px solid ${form.enabled ? AID.gold : '#d1d5db'}`,
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 14,
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: AID.brownDeep }}>
                Thème festif
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {form.enabled ? 'Activé' : 'Désactivé'}
              </div>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={e => patch('enabled', e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              />
              <span style={{
                position: 'absolute', inset: 0,
                background: form.enabled ? AID.gold : '#d1d5db',
                borderRadius: 12,
                transition: 'background 0.2s',
              }} />
              <span style={{
                position: 'absolute',
                top: 3,
                left: form.enabled ? 23 : 3,
                width: 18,
                height: 18,
                background: '#fff',
                borderRadius: '50%',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </label>
          </div>

          {/* Date de l'Aid */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: AID.brownDeep, marginBottom: 5 }}>
              Date de l'Aid (J)
            </label>
            <input
              type="date"
              value={form.aidDate || ''}
              onChange={e => patch('aidDate', e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px',
                borderRadius: 7,
                border: `1.5px solid ${AID.gold}`,
                background: '#fffef5',
                fontSize: 13,
                color: AID.brownDeep,
                fontWeight: 600,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Jours avant / après */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: AID.brownDeep, marginBottom: 5 }}>
                Jours avant (J-X)
              </label>
              <input
                type="number"
                min={0}
                max={7}
                value={form.daysBefore ?? 1}
                onChange={e => patch('daysBefore', Number(e.target.value))}
                style={numStyle(AID)}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: AID.brownDeep, marginBottom: 5 }}>
                Jours après (J+X)
              </label>
              <input
                type="number"
                min={0}
                max={14}
                value={form.daysAfter ?? 3}
                onChange={e => patch('daysAfter', Number(e.target.value))}
                style={numStyle(AID)}
              />
            </div>
          </div>

          {/* Aperçu période */}
          {form.aidDate && (
            <div style={{
              fontSize: 11,
              color: previewActive ? '#065f46' : '#6b7280',
              background: previewActive ? '#d1fae5' : '#f3f4f6',
              border: `1px solid ${previewActive ? '#6ee7b7' : '#e5e7eb'}`,
              borderRadius: 6,
              padding: '7px 10px',
              marginBottom: 12,
            }}>
              <span style={{ fontWeight: 700 }}>
                {previewActive ? '🟢 Actif maintenant' : '⚫ Inactif'}
              </span>
              {period && <span style={{ marginLeft: 6 }}>· {period}</span>}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 1,
                padding: '9px 0',
                background: `linear-gradient(135deg, ${AID.goldDeep}, ${AID.gold})`,
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                fontWeight: 700,
                fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              onClick={() => { setForm({ ...aidSettings }); setMessage(''); }}
              style={{
                padding: '9px 14px',
                background: '#fff',
                color: AID.brownDeep,
                border: `1.5px solid ${AID.gold}`,
                borderRadius: 7,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Reset
            </button>
          </div>

          {message && (
            <div style={{
              marginTop: 10,
              fontSize: 12,
              textAlign: 'center',
              color: message.startsWith('✅') ? '#065f46' : '#b91c1c',
              fontWeight: 600,
            }}>
              {message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const numStyle = (AID) => ({
  width: '100%',
  padding: '7px 10px',
  borderRadius: 7,
  border: `1.5px solid ${AID.gold}`,
  background: '#fffef5',
  fontSize: 14,
  fontWeight: 700,
  color: AID.brownDeep,
  textAlign: 'center',
  boxSizing: 'border-box',
});

export default AidSettingsPanel;
